<template>
  <div class="detail-section" :class="{ 'loading-size': isLoading }">
    <div class="m-2 text-center" v-if="isLoading">
      <font-awesome-icon
        :icon="['fa', 'circle-notch']"
        class="fa-spin m-auto spinner fa-5x egx-partner-color"
      />
    </div>
    <div :class="[{'content-box': !disableCard}, { 'no-card': disableCard }, 'mb-2']" v-else>
      <div class="card-wrapper">
        <template v-if="filterList.length > 0">
          <div
            class="btn btn-link filter-link"
            @click="$bvModal.show('measure-filter')"
          >
            Filter by Measure Categories
          </div>
          <br />

          <div class="selected-tags-list mt-1">
            <div
              class="tags-content-box row btn btn-egx-white mr-3 ml-auto"
              v-for="item in selectedFilters"
              :key="item"
            >
              {{ item }}
              <span
                class="ml-2"
                role="button"
                @click="removeFilters(item)"
              >
                <font-awesome-icon :icon="['fa', 'times']" />
              </span>
            </div>
          </div>
        </template>
        <div v-for="(groupedMeasure, index) in groupedMeasures" :key="groupedMeasure.id">
          <div class="m-0" v-show="groupFilterList.includes(groupedMeasure.id)">
            <div class="content-box content-box-plus child row my-3 pl-4">
              <div class="mb-2 ">
                <div class="title">
                  {{ groupedMeasure.name }}
                </div>
                <div class="description" v-safe-html="groupedMeasure.measure_instruction" />
              </div>

              <div class="container p-0 py-2">
                <div class="incentives-list">
                  <div class="mr-3" v-for="(retrofit, index) in groupedMeasure.retrofits" :key="retrofit.id">
                    <div v-if="checkFilter(retrofit.hierarchy_level_1, groupedMeasure.id)">
                      <hr v-show="index !== 0" />
                      <div class="d-flex row form">
                        <div class="pr-4 col-md-9 col-sm-12">
                          <div class="sub-title mb-0">{{ retrofit.name }}</div>
                          <div class="mb-0 field-label">
                            <div>{{ retrofit.hierarchy_level_1 }}</div>
                            <div class="pt-2" v-safe-html="retrofit.program_specific_measure_details" />
                          </div>
                        </div>
                        <div class="button d-flex flex-column justify-content-center col-md-3 col-sm-12">
                          <div class="btn mb-1 btn-egx w-100" @click="selectMeasure(retrofit)">
                            {{ selectMeasureSettings?.text || 'Select Measure' }}
                          </div>
                          <div
                            class="btn btn-egx-white"
                            v-if="!!pageLearnMoreSettings?.display"
                          >
                            Learn More
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <step-button
      :steps-response="stepsResponse"
      @navigate="navigate"
      @save-data="nextStep"
      :data-save-required="true"
      v-if="!isLoading"
    />
    <measure-filter-modal
      :selected-filters="selectedFilters"
      :filter-list="filterList"
      @update-selected-filters="updateSelectedFilters"
    />
    <measure-apply-modal
      :project-id="projectId"
      :selected-measure="selectedMeasure"
      :fulfillSyncDetails="fulfillSyncDetails"
      @on-hide="resetSelectedMeasures"
    />
  </div>
</template>

<script>
import MeasureFilterModal from '@/components/project/MeasureFilterModal.vue';
import MeasureApplyModal from '@/components/project/MeasureApplyModal.vue';
import StepButton from '@/components/project/stepButton.vue';
import AccordionTabList from '@/components/project/AccordionTabList.vue';
import projectMixins from '@/shared/mixins/projectMixins';

export default {
  components: {
    AccordionTabList,
    StepButton,
    MeasureFilterModal,
    MeasureApplyModal
  },

  props: {
    projectId: {
      type: String,
      required: true,
    },
    content: Object,
    stepsResponse: Object,
    navigate: Function,
    projectStatus: String,
    stepConfiguration: Object,
  },
  mixins: [projectMixins],

  data() {
    return {
      disableCard: true,
      isLoading: false,
      groupedMeasures: [],
      selectedFilters: [],
      filterList: [],
      groupFilterList: [],
      selectedMeasure: null,
    };
  },

  created() {
    this.isLoading = true;
    this.$store.dispatch('fulfill_integrate/get_measures', { project_id: this.projectId, project_status: this.projectStatus })
      .then((response) => {
        this.groupedMeasures = this.sortedList(response);
        this.groupedMeasures = this.groupedMeasures.map((group) => {
          return {
            ...group,
            retrofits: this.sortedList(group.retrofits)
          };
        });
        this.collectFilters();
        this.filterList = this.filterList.sort();
      }).catch((error) => {
        this.$notify({
          group: 'notice',
          text: error?.message,
        });
      }).finally(() =>{
        this.isLoading = false;
      });
  },

  computed: {
    settings() {
      return this.$store.state.partners.currentPartner.settings;
    },

    fulfillSyncDetails() {
      return this.settings.fulfill_sync_details;
    },

    pageLearnMoreSettings() {
      return this.fulfillSyncDetails?.pages?.measure?.learn_more;
    },

    selectMeasureSettings() {
      return this.fulfillSyncDetails?.pages?.measure?.select_measure;
    },

    currentProject() {
      return this.$store.state.fulfill_integrate.currentSelectedProject
    },
  },

  methods: {
    removeFilters(item) {
      this.groupFilterList = [];
      this.selectedFilters = this.selectedFilters.filter((selectedItem) => { return selectedItem !== item })
    },

    updateSelectedFilters(allSelectedFilters) {
      this.groupFilterList = [];
      this.selectedFilters = allSelectedFilters;
    },

    sortedList(list) {
      return list.sort((a, b) => a.sorting_tag - b.sorting_tag);
    },

    checkFilter(value, groupId) {
      const check = (this.selectedFilters.length === 0) || this.selectedFilters.includes(value);

      if(check && this.groupFilterList.indexOf(groupId) === -1) { this.groupFilterList.push(groupId); }

      return check;
    },

    collectFilters() {
      this.groupedMeasures.forEach((groupMeasure) => {
        groupMeasure.retrofits.forEach((measure) => {
          if(this.filterList.indexOf(measure.hierarchy_level_1) === -1 && measure.hierarchy_level_1) {
            this.filterList.push(measure.hierarchy_level_1);
          }
        })
      });
    },

    nextStep(step) {
      this.checkLockedProject();
      if (step === 'nextStep') {
        this.navigate();
      } else if (step === 'linkStep') {
        this.redirectPath(this.stepsResponse.add_btn_url);
      }
    },

    selectMeasure(measure) {
      if (this.locked(this.currentProject)) {
        this.$router.push('/applications');
        return;
      }
      this.selectedMeasure = measure;
      this.$nextTick(() => {
        this.$bvModal.show('measure-apply');
      });
    },

    resetSelectedMeasures() {
      this.selectedMeasure = null;
    },

    checkLockedProject() {
      this.$store.dispatch('fulfill_integrate/get_project_selected', { project_id: this.currentProject.id })
      .then((response) => {
        if(this.locked(response.project)) {
          this.$router.push('/applications?errorPersist=true');
          return;
        }
        return false;
      })
      .catch((error) => {
        console.log(error);
        this.$router.push({ name: 'Offers' });
      });
    },
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/stylesheets/components/project/measures.scss';
@import '../../assets/stylesheets/components/project/contact.scss';

</style>
